import { lazy, ComponentType } from 'react';

/**
 * Wraps React.lazy with chunk load error recovery.
 * On failure (stale deployment), reloads the page once to fetch fresh chunks.
 * Prevents infinite reload loops via sessionStorage flag.
 */
export function lazyWithRetry(importFn: () => Promise<{ default: ComponentType<any> }>) {
	return lazy(() => {
		const reloadKey = 'chunk_reload_' + window.location.pathname;

		return importFn()
			.then((module) => {
				sessionStorage.removeItem(reloadKey);
				return module;
			})
			.catch((error: Error) => {
				const isChunkError =
					error.name === 'ChunkLoadError' ||
					error.message?.includes('Loading chunk') ||
					error.message?.includes('Failed to fetch dynamically imported module');

				if (isChunkError) {
					const hasReloaded = sessionStorage.getItem(reloadKey);

					if (!hasReloaded) {
						sessionStorage.setItem(reloadKey, '1');
						window.location.reload();
						// eslint-disable-next-line @typescript-eslint/no-empty-function
						return new Promise(() => {
							/* intentional no-op — page is reloading */
						});
					}
					// Already reloaded once — clear flag and let error propagate
					sessionStorage.removeItem(reloadKey);
				}

				throw error;
			});
	});
}
